/**
 * Operator-tunable reaper throughput limits (#2681).
 *
 * Plain `.mjs` (like `retention-days.mjs`) so the deploy-script test can pin
 * `scripts/deploy-gateway-vps.sh`'s validator against these exact ceilings.
 * The DEFAULTS stay where they always lived (the sibling contract constants
 * and the gateway's trace constants); this module only adds the ceiling and
 * the resolver, so an unset knob is byte-identical to the code before #2681.
 *
 * ── HOW EACH CEILING IS DERIVED ─────────────────────────────────────────────
 *
 * The clocks every reaper tick lives inside:
 *   - control-loops kills a handler at 25 s (`CONTROL_LOOP_HANDLER_TIMEOUT_MS`;
 *     none of these handlers overrides it);
 *   - each gateway POST is bounded by the 10 s service fetch timeout
 *     (`CONTROL_LOOP_SERVICE_FETCH_TIMEOUT_MS`);
 *   - every DELETE / SELECT carries `SET LOCAL statement_timeout` (5 s for the
 *     lifecycle and usage_logs reapers, 1.5 s for traces).
 * A loop checks its wall-clock budget BEFORE each batch, so one call can run
 * at most `budget + one statement_timeout` (the last batch admitted at the
 * last moment; the age read is skipped once the budget is spent).
 *
 * Every ceiling is 2x the default. That is the conservative side of the
 * issue's 2-4x traffic target: batch and fuse ceilings multiply, so rows per
 * tick can still reach 4x (2x rows per batch x 2x batches) while each single
 * statement and each single call stays inside its measured envelope.
 *
 *   TIME BUDGET 3 000 ms (default 1 500). Lifecycle metrics/events, per region:
 *     3 000 + 5 000 = 8 000 ms < 10 000 ms fetch bound (2 s left for HTTP and
 *     the metrics route's two 500-row side DELETEs); jp + cn serial = 16 000 ms,
 *     9 s under the 25 s kill. Traces: 3 000 + 1 500 = 4 500 ms per region,
 *     9 000 ms serial. CN usage_logs (control-loops budget): the ceiling adds
 *     1.5 s to today's worst case of one last-moment page (three HTTPS legs,
 *     each statement-bound at 5 s): 3 + 15 = 18 s < 25 s.
 *   MAX BATCHES / MAX PAGES 16 (default 8). A fuse, not a clock: the budget is
 *     checked before every batch, so raising it cannot extend a call past
 *     `budget + one statement_timeout`. It only bounds rows per call.
 *   BATCH ROWS 10 000 (default 5 000). One lifecycle DELETE of 5 000 rows took
 *     ~0.75 s on Tokyo (2026-09-26: 10k rows in two batches inside the 1.5 s
 *     budget, #2681), so 10 000 is ~1.5 s against its 5 s statement_timeout.
 *   TRACE BATCH ROWS 4 000 (default 2 000). The pre-existing hard clamp in
 *     `cleanupExpiredMessageTraces` (`TRACE_CLEANUP_BATCH_MAX`); the knob can
 *     never ask for more than the function already refuses.
 *   USAGE_LOGS PAGE IDS 4 000 (default 2 000). Shanghai is a 2-core PL0 host;
 *     the contract note stands: re-measure it before raising past 2 000. The
 *     ceiling bounds each id list (SELECT, Tokyo confirm, CN DELETE, all under
 *     5 s statement_timeout) at 2x what production runs today.
 *   RATING EVENTS MAX ROWS 40 000 (default 20 000). Hourly page; 4x today's
 *     8.3k/h peak is 33k/h, inside one page. Each of the three legs is one
 *     statement over at most 40k ids, 2x today, under the 10 s fetch bound.
 *   RATING EVENTS CN CHUNK ROWS 40 000 (default 20 000, the default page).
 *     Ids per CN allowlist DELETE call. A clock-neutral knob: the handler
 *     gives the CN leg a fixed deadline (select + CN together inside two
 *     10 s legs) and only starts another chunk while that deadline allows,
 *     so a smaller chunk never extends the 35 s handler kill; at or above
 *     the page size it is one call, as before the knob.
 *
 * ALLOWLIST CAPS USE THE CEILING, NEVER THE KNOB. `usage_events` and
 * `usage_logs` reapers hand an id list from one region/host to another. If a
 * receiving side capped at its own (tunable) page size, a rollout that left
 * the regions configured differently would truncate a list the other side
 * already acted on — the CN-first `usage_events` order would orphan CN rows,
 * and a short Tokyo confirm would push confirmed ids into quarantine. The
 * routes therefore cap those lists at the ceiling; only the SELECT page size
 * is tunable.
 */

/** @type {Readonly<Record<import('./reaper-limits.d.mts').ReaperLimitName, number>>} */
export const REAPER_LIMIT_CEILINGS = Object.freeze({
  // gateway: POST /internal/lifecycle-metrics/cleanup
  LIFECYCLE_METRICS_CLEANUP_BATCH_ROWS: 10_000,
  LIFECYCLE_METRICS_CLEANUP_MAX_BATCHES: 16,
  LIFECYCLE_METRICS_CLEANUP_TIME_BUDGET_MS: 3_000,
  // gateway: POST /internal/lifecycle-events/cleanup
  LIFECYCLE_EVENTS_CLEANUP_BATCH_ROWS: 10_000,
  LIFECYCLE_EVENTS_CLEANUP_MAX_BATCHES: 16,
  LIFECYCLE_EVENTS_CLEANUP_TIME_BUDGET_MS: 3_000,
  // gateway: POST /internal/message-traces/cleanup
  TRACE_CLEANUP_BATCH_ROWS: 4_000,
  TRACE_CLEANUP_MAX_BATCHES: 16,
  TRACE_CLEANUP_TIME_BUDGET_MS: 3_000,
  // gateway: POST /internal/usage-events/cleanup (Tokyo select page)
  RATING_EVENTS_CLEANUP_MAX_ROWS: 40_000,
  // control-loops: rating_events_cleanup handler (ids per CN delete call)
  RATING_EVENTS_CLEANUP_CN_CHUNK_ROWS: 40_000,
  // gateway: POST /internal/usage-logs/cleanup (CN select page)
  USAGE_LOGS_CLEANUP_PAGE_IDS: 4_000,
  // control-loops: usage_logs_cn_cleanup handler
  USAGE_LOGS_CN_CLEANUP_MAX_PAGES: 16,
  USAGE_LOGS_CN_CLEANUP_TIME_BUDGET_MS: 3_000,
});

/**
 * Resolve one knob. Never throws, never applies a bad value:
 *
 * - unset / blank string → `defaultValue`, no warning (today's behaviour);
 * - a decimal integer in `[1, ceiling]` → that integer;
 * - anything else → `defaultValue` plus a `configWarning` naming the knob, the
 *   raw value and why, for the route/handler to put on its outcome.
 *
 * A `number` only ever arrives from in-process injection (route and handler
 * unit tests; the Node runtime and the Worker hand over the raw env string).
 * It keeps the pre-#2681 test-only semantics — a finite non-negative number,
 * floored, else the default — so `0` still means "admit no batch" in tests.
 *
 * @param {import('./reaper-limits.d.mts').ReaperLimitName} name
 * @param {unknown} raw
 * @param {number} defaultValue
 * @returns {import('./reaper-limits.d.mts').ResolvedReaperLimit}
 */
export function resolveReaperLimit(name, raw, defaultValue) {
  const ceiling = REAPER_LIMIT_CEILINGS[name];
  if (ceiling === undefined) {
    throw new Error(`unknown reaper limit ${name}`);
  }
  if (raw === undefined || raw === null) return { value: defaultValue };
  if (typeof raw === 'number') {
    return { value: Number.isFinite(raw) && raw >= 0 ? Math.floor(raw) : defaultValue };
  }
  const trimmed = String(raw).trim();
  if (trimmed === '') return { value: defaultValue };
  if (!/^[1-9][0-9]*$/u.test(trimmed)) {
    return {
      value: defaultValue,
      configWarning: `${name}=${raw} ignored: not an integer in [1, ${ceiling}]`,
    };
  }
  const value = Number(trimmed);
  if (!(value <= ceiling)) {
    return { value: defaultValue, configWarning: `${name}=${raw} ignored: exceeds ceiling ${ceiling}` };
  }
  return { value };
}

/**
 * @param {readonly import('./reaper-limits.d.mts').ResolvedReaperLimit[]} resolved
 * @returns {string[]}
 */
export function reaperConfigWarnings(resolved) {
  return resolved
    .map((entry) => entry.configWarning)
    .filter((warning) => typeof warning === 'string');
}
